/**
 * @file `CampaignDetailPanel` — the `/emails/campaigns/:id` route: one
 *       campaign's delivery detail — aggregate tiles, delivery-state filter
 *       chips, a per-recipient delivery timeline, and the sent email.
 * @module @labcraft/admin/ui/emails/campaign-detail-panel
 *
 * Aggregates + chips are computed client-side from the recipient rows (the
 * same worst-first reduction the delivery pill uses). The recipient table is
 * a `LowerSplit`: selecting a row opens a bottom pane with that recipient's
 * delivery timeline (sent → delivered → clicked / bounced / complained). The
 * whole email body is behind a "View email" drawer rather than inline. The
 * resend-to-bounced action re-sends the body to the bounced + failed set.
 */

import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type JSX,
} from 'react'
import { Link, useParams } from 'react-router'
import type { CampaignRecipient } from '../../shared/emails_routes.ts'
import {
  useCampaign,
  useEmailFrom,
  usePagedList,
  useResendCampaignMutation,
} from '../hooks/index.ts'
import { DeliveryPill, deliveryFor } from './delivery_pill.tsx'
import { DeliveryTimeline } from './delivery_timeline.tsx'
import { Drawer } from './drawer.tsx'
import { EmailPreview } from './email_preview.tsx'
import { LowerSplit } from '../kernel/lower_split.tsx'
import { Pager } from '../kernel/pager.tsx'

const COLUMNS = ['Email', 'Delivery', 'Provider id', 'Last event'] as const

/** Delivery-state filter chips (all + the worst-first pill labels). */
const STATE_FILTERS = [
  'all',
  'sent',
  'delivered',
  'clicked',
  'delayed',
  'bounced',
  'complained',
  'failed',
] as const
type StateFilter = (typeof STATE_FILTERS)[number]

/** Pick the most recent event timestamp on a recipient record. */
function lastEvent(r: CampaignRecipient): string | null {
  const stamps = [
    r.complainedAt,
    r.bouncedAt,
    r.deliveredAt,
    r.delayedAt,
  ].filter((s): s is string => typeof s === 'string')
  if (stamps.length === 0) return null
  return stamps.sort().at(-1) ?? null
}

/** `2026-05-21T12:00:00Z` → `May 21, 12:00`; invalid → em dash. */
function formatStamp(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

/** Render the per-campaign delivery detail route. */
export function CampaignDetailPanel(): JSX.Element {
  const { id } = useParams<{ id: string }>()
  const result = useCampaign(id ?? '')
  const resend = useResendCampaignMutation()
  const previewFrom = useEmailFrom(
    result.data?.classification === 'marketing' ? 'marketing' : 'transactional',
  )
  const [filter, setFilter] = useState<StateFilter>('all')
  const [sel, setSel] = useState<string | null>(null)
  const [viewOpen, setViewOpen] = useState(false)

  const campaign = result.data ?? null
  const error = result.isError
    ? result.error.message || 'Could not load this campaign.'
    : null

  const recipients = campaign?.recipients ?? []

  // Aggregate tiles, computed from the recipient rows (clicked is a subset of
  // delivered; delayed/bounced/etc. are non-exclusive delivery signals).
  const agg = useMemo(() => {
    const c = {
      delivered: 0,
      clicked: 0,
      delayed: 0,
      bounced: 0,
      complained: 0,
      failed: 0,
    }
    for (const r of recipients) {
      if (r.sendStatus === 'failed') c.failed += 1
      if (r.deliveryState === 'complained') c.complained += 1
      if (r.deliveryState === 'bounced') c.bounced += 1
      if (r.clickedAt) c.clicked += 1
      if (r.deliveryState === 'delivered' || r.deliveredAt) c.delivered += 1
      if (r.deliveryState === 'delivery_delayed' || r.delayedAt) c.delayed += 1
    }
    return c
  }, [recipients])

  const filtered = useMemo(
    () =>
      filter === 'all'
        ? recipients
        : recipients.filter(r => deliveryFor(r).label === filter),
    [recipients, filter],
  )

  const scrollRef = useRef<HTMLDivElement>(null)
  const { page, setPage, resetPage, pageSize, rowHeight, offset, pageCount } =
    usePagedList(scrollRef, filtered.length)
  // A delivery-state chip change starts a fresh page.
  useEffect(() => {
    resetPage()
  }, [filter, resetPage])
  const paged = filtered.slice(offset, offset + pageSize)

  const selected = useMemo(
    () => recipients.find(r => r.recipientId === sel) ?? null,
    [recipients, sel],
  )

  const retryable = useMemo(
    () =>
      recipients.filter(
        r => r.deliveryState === 'bounced' || r.sendStatus === 'failed',
      ).length,
    [recipients],
  )

  const resendError = resend.isError
    ? resend.error.message || 'Resend failed.'
    : null

  const tiles: ReadonlyArray<{ label: string; value: number; tone?: string }> =
    campaign
      ? [
          { label: 'Recipients', value: campaign.recipientCount },
          { label: 'Delivered', value: agg.delivered },
          { label: 'Clicked', value: agg.clicked },
          { label: 'Delayed', value: agg.delayed, tone: 'warn' },
          { label: 'Bounced', value: agg.bounced, tone: 'bad' },
          { label: 'Complained', value: agg.complained, tone: 'bad' },
          { label: 'Failed', value: agg.failed, tone: 'bad' },
          { label: 'Suppressed', value: campaign.suppressedCount },
        ]
      : []

  const table = (
    <div
      className="table-scroll"
      ref={scrollRef}
      style={
        rowHeight > 0
          ? ({ '--row-h': `${rowHeight}px` } as CSSProperties)
          : undefined
      }>
      <table className="table">
        <thead>
          <tr>
            {COLUMNS.map(c => (
              <th key={c}>{c}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {result.isPending && campaign === null && (
            <tr>
              <td className="empty-row" colSpan={COLUMNS.length}>
                // loading…
              </td>
            </tr>
          )}
          {campaign && filtered.length === 0 && (
            <tr>
              <td className="empty-row" colSpan={COLUMNS.length}>
                // no recipients in this state.
              </td>
            </tr>
          )}
          {paged.map(r => {
            const stamp = lastEvent(r)
            return (
              <tr
                key={r.recipientId}
                className="is-clickable"
                aria-selected={sel === r.recipientId}
                onClick={() => setSel(r.recipientId)}>
                <td className="cell-email">{r.email}</td>
                <td>
                  <DeliveryPill recipient={r} />
                </td>
                <td className="cell-meta cell-code">
                  {r.providerMessageId ?? '—'}
                </td>
                <td className="cell-meta">
                  {stamp ? formatStamp(stamp) : '—'}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )

  const detail = selected && (
    <div className="rcp-detail">
      <div className="rcp-detail__head">
        <span className="cell-email">{selected.email}</span>
        <DeliveryPill recipient={selected} />
      </div>
      <dl className="kv-grid">
        <dt>provider id</dt>
        <dd className="cell-code">{selected.providerMessageId ?? '—'}</dd>
        <dt>submitted</dt>
        <dd>{selected.createdAt ? formatStamp(selected.createdAt) : '—'}</dd>
      </dl>
      <h4 className="aside-h">Delivery timeline</h4>
      <DeliveryTimeline recipient={selected} />
    </div>
  )

  return (
    <div className="panel emails-panel emails-panel--paged">
      <header className="panel-head">
        <div>
          <div className="panel-head__title-row">
            <h1 className="panel-head__title">
              {campaign ? campaign.subject || '(no subject)' : 'Campaign'}
            </h1>
            <span className="panel-head__count">
              {campaign ? `${campaign.recipientCount} recipients` : '—'}
            </span>
          </div>
          <p className="panel-head__sub">
            <Link to="/emails/campaigns">← all campaigns</Link>
          </p>
        </div>
        <div className="panel-head__action">
          {campaign && (
            <button
              type="button"
              className="lc-btn lc-btn--ghost"
              onClick={() => setViewOpen(true)}>
              View email
            </button>
          )}
          {campaign && retryable > 0 && (
            <button
              type="button"
              className="lc-btn lc-btn--primary"
              disabled={resend.isPending}
              onClick={() => {
                resend.reset()
                resend.mutate(campaign.id)
              }}>
              {resend.isPending ? 'Resending…' : `Resend ${retryable} bounced`}
            </button>
          )}
        </div>
      </header>

      {error !== null && (
        <div className="panel-banner panel-banner--error">
          <b>Could not load this campaign.</b> {error}
        </div>
      )}
      {resend.data && (
        <div className="panel-banner panel-banner--ok">
          <b>Resent.</b> {resend.data.accepted} accepted — delivery runs in the
          background{' '}
          {resend.data.campaignId !== campaign?.id && (
            <Link to={`/emails/campaigns/${resend.data.campaignId}`}>
              — view the retry campaign →
            </Link>
          )}
        </div>
      )}
      {resendError !== null && (
        <div className="panel-banner panel-banner--error">
          <b>Resend failed.</b> {resendError}
        </div>
      )}

      {campaign && (
        <div className="agg">
          {tiles.map(t => (
            <div
              key={t.label}
              className={
                t.tone ? `agg__item agg__item--${t.tone}` : 'agg__item'
              }>
              <span className="agg__n">{t.value}</span>
              <span className="agg__l">{t.label}</span>
            </div>
          ))}
        </div>
      )}

      {campaign && (
        <div
          className="chips emails-statechips"
          role="group"
          aria-label="Filter by delivery state">
          {STATE_FILTERS.map(s => (
            <button
              key={s}
              type="button"
              className="chip"
              aria-pressed={filter === s}
              onClick={() => setFilter(s)}>
              {s}
            </button>
          ))}
        </div>
      )}

      <LowerSplit
        open={selected !== null}
        storageKey="emails-campaign-detail"
        detail={detail}>
        <div className="paged-list">
          {table}
          <div className="paged-foot">
            <Pager
              page={page}
              pageCount={pageCount}
              onPage={setPage}
              total={campaign ? filtered.length : undefined}
            />
          </div>
        </div>
      </LowerSplit>

      {campaign && (
        <Drawer
          open={viewOpen}
          onClose={() => setViewOpen(false)}
          kicker="Campaign"
          title={campaign.subject || '(no subject)'}>
          {campaign.format === 'html' ? (
            <EmailPreview
              from={previewFrom}
              subject={campaign.subject}
              bodyHtml={campaign.body}
              device="desktop"
              footer={campaign.classification === 'marketing'}
            />
          ) : (
            <pre className="review-body">{campaign.body}</pre>
          )}
        </Drawer>
      )}
    </div>
  )
}
